import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { InfoIcon, CheckCircle2, FolderTree, Settings as SettingsIcon } from 'lucide-react';
import { useBudgetConfiguration } from '@/hooks/use-budget-configuration';
import { BudgetTypeSelector } from '@/components/budget-configuration';
import type { BudgetTrackingType } from '@/types/budget-configuration.types';

export function SettingsBudgetConfigurationPage() {
  const navigate = useNavigate();
  const {
    trackingType,
    hasUnsavedChanges,
    isSaving,
    updateTrackingType,
    saveConfiguration,
    discardChanges,
  } = useBudgetConfiguration();

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const breadcrumbItems = [
    { label: 'Settings', onClick: () => navigate('/settings') },
    { label: 'Projects', onClick: () => navigate('/settings/projects'), icon: <FolderTree className="h-3 w-3" /> },
    { label: 'Budget Configuration', icon: <SettingsIcon className="h-3 w-3" /> },
  ];

  const handleTypeChange = (type: BudgetTrackingType) => {
    updateTrackingType(type);
  };

  const handleSave = async () => {
    const result = await saveConfiguration();
    if (result.success) {
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };

  const handleDiscard = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      navigate('/settings/projects');
    }
  };

  const confirmDiscard = () => {
    discardChanges();
    setShowDiscardDialog(false);
    navigate('/settings/projects');
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Budget Configuration</h1>
          <p className="text-muted-foreground mt-1">
            Configure budget tracking for all budget types
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDiscard}>
            {hasUnsavedChanges ? 'Discard' : 'Back'}
          </Button>
          <Button onClick={handleSave} disabled={!hasUnsavedChanges || isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Success message */}
      {showSuccessMessage && (
        <Alert className="border-success-200 bg-success-50 dark:border-success-900 dark:bg-success-950">
          <CheckCircle2 className="h-4 w-4 text-success-600 dark:text-success-400" />
          <AlertDescription className="text-success-900 dark:text-success-100">
            Configuration saved successfully! Budget tracking has been updated.
          </AlertDescription>
        </Alert>
      )}

      {/* Unsaved changes indicator */}
      {hasUnsavedChanges && (
        <Alert>
          <InfoIcon className="h-4 w-4" />
          <AlertDescription>
            You have unsaved changes. Click "Save Changes" to apply your configuration.
          </AlertDescription>
        </Alert>
      )}

      {/* Configuration Info */}
      <Alert className="border-info-200 bg-info-50 dark:border-info-900 dark:bg-info-950">
        <InfoIcon className="h-4 w-4 text-info-600 dark:text-info-400" />
        <AlertDescription className="text-info-900 dark:text-info-100">
          <strong>Global Configuration:</strong> This setting applies to all budget types
          (Resource, Sales, and Cost) to ensure consistent tracking and reporting across
          your projects.
        </AlertDescription>
      </Alert>

      {/* Budget Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Tracking Type</CardTitle>
          <CardDescription>
            Choose how all budgets will be structured when editing individual projects
          </CardDescription>
        </CardHeader>
        <CardContent>
          <BudgetTypeSelector value={trackingType} onChange={handleTypeChange} />
        </CardContent>
      </Card>

      {/* What This Affects */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success-600" />
            What This Affects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">💼 Resource Budgets</h4>
                <p className="text-sm text-muted-foreground">
                  Role hours allocation and team member assignment
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">💰 Sales Budgets</h4>
                <p className="text-sm text-muted-foreground">
                  Income tracking and sales forecasting
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">💸 Cost Budgets</h4>
                <p className="text-sm text-muted-foreground">
                  Expense tracking and cost management
                </p>
              </div>
            </div>
            <Alert className="bg-muted/50">
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                All budget types will use the same tracking structure for consistent
                                reporting and analytics. This ensures your resource allocations, sales
                                projections, and cost tracking are aligned.
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>

      {/* What Happens Next */}
      <Card>
        <CardHeader>
          <CardTitle>What Happens Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            {trackingType === 'simple' && (
              <>
                <p>
                  <strong>When editing a project's budget:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                  <li>Add roles and assign total hours per role</li>
                  <li>Set rates for cost estimation</li>
                  <li>Track sales and costs with simple totals</li>
                  <li>Ideal for shorter projects or when monthly detail isn't needed</li>
                </ul>
              </>
            )}
            {trackingType === 'monthly' && (
              <>
                <p>
                  <strong>When editing a project's budget:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                  <li>Add roles and assign total hours per role</li>
                  <li>Allocate hours across project months</li>
                  <li>Track sales and costs with monthly breakdown</li>
                  <li>Ideal for longer projects requiring detailed timeline planning</li>
                </ul>
              </>
            )}
            {trackingType === 'none' && (
              <>
                <p>
                  <strong>Budget tracking is disabled:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                  <li>Projects will not track budgets</li>
                  <li>Budget tabs will be hidden or disabled</li>
                  <li>You can enable tracking at any time by changing this setting</li>
                </ul>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Discard Changes Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to your budget configuration. Are you sure you want
              to discard these changes?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard}>Discard Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
